import {
  AUTHENTICATED,
  AuthContext,
  getEnv,
  logger,
  type CurrentAuthState,
} from "@bfc-software/app-toolbox";
import { MenuListItem } from "@bfc-software/pallet";
import {
  createContext,
  useContext,
  useMemo,
  useRef,
  type ReactNode,
} from "react";
import createFetchClient, { type Client, type Middleware } from "openapi-fetch";
import createClient from "openapi-react-query";
import { queryClient } from "@bfc-software/bfc-web-ui/lib/aaa-api-client";

/** What the dev-only "force session expiry" item talks to the middleware through. Module-private. */
type ForcedExpiry = {
  /** Make the next response (from any authenticated client) count as a 401. */
  arm: () => void;
  /** True once after `arm()`; resets itself so the next sign-in is not immediately expired. */
  consume: () => boolean;
};

// Default = inert, so trees without the provider (all existing tests) are unchanged.
const ForcedExpiryContext = createContext<ForcedExpiry>({
  arm: () => {},
  consume: () => false,
});

/**
 * Ref-backed on purpose: the flag is read at response time, so arming needs no
 * re-render and does not rebuild every authenticated client.
 */
export function ForcedExpiryProvider({ children }: { children: ReactNode }) {
  const armed = useRef(false);
  const value = useMemo<ForcedExpiry>(
    () => ({
      arm: () => {
        armed.current = true;
      },
      consume: () => {
        const was = armed.current;
        armed.current = false;
        return was;
      },
    }),
    [],
  );
  return (
    <ForcedExpiryContext.Provider value={value}>
      {children}
    </ForcedExpiryContext.Provider>
  );
}

/** Returns the function that arms a forced expiry. Exported for the menu item and for tests. */
export function useForceSessionExpiry(): () => void {
  return useContext(ForcedExpiryContext).arm;
}

/** Development-only account menu item: the next authenticated response is treated as a 401. */
export function ForceSessionExpiryMenuItem() {
  const arm = useForceSessionExpiry();
  if (getEnv().environment !== "development") return null;
  return (
    <MenuListItem
      textValue="Force session expiry (dev)"
      onAction={() => {
        arm();
        // Refetch active queries now so the forced 401 fires without the user navigating.
        void queryClient.invalidateQueries();
      }}
    >
      {"Force session expiry (dev)"}
    </MenuListItem>
  );
}

export type AuthenticatedState = Extract<
  CurrentAuthState,
  { status: typeof AUTHENTICATED }
>;

/**
 * The authenticated auth state. Throws otherwise: every caller sits under the
 * org gate, which unmounts its subtree on the render that sees a signed-out
 * state, so reaching the throw is a bug, not a redirect.
 */
export function useAssertedAuth(): AuthenticatedState {
  const { state } = useContext(AuthContext);
  if (state.status !== AUTHENTICATED) {
    throw new Error("useAssertedAuth called outside an authenticated subtree");
  }
  return state;
}

/** Token selectors, one per module, preserving each module's historical choice. `undefined` sends no auth header. */
export type TokenSelector = (s: AuthenticatedState) => string | undefined;
export const aaaTokenOrToken: TokenSelector = (s) =>
  s.sessionData.aaaToken || s.token;
export const aaaTokenOnly: TokenSelector = (s) => s.sessionData.aaaToken;

const bearer = (t: string) => ({ Authorization: `Bearer ${t}` });

/**
 * The services answer an expired session JWT with a 401
 * (`{"error":"error","message":"token expired"}`). Nothing a screen can do
 * recovers from that, so sign out. The org gate renders a `<Navigate/>` to
 * login once the state is no longer authenticated, so this does not navigate.
 *
 * Not for the AAA client: its sign-in endpoints answer a wrong password with a
 * 401 too.
 */
export function useSessionExpiryMiddleware(): Middleware {
  const { logout } = useContext(AuthContext);
  const forced = useContext(ForcedExpiryContext);
  return useMemo<Middleware>(
    () => ({
      onResponse({ response }) {
        const wasForced = forced.consume(); // always consume: the flag is one-shot
        if (!wasForced && response.status !== 401) return;
        logger.warn(
          wasForced
            ? "Session expiry forced (dev), signing out"
            : "Session rejected with 401, signing out",
        );
        logout();
        // Last: drop the dead session's cache.
        queryClient.clear();
        // Forced: hand the caller what a real expiry produces. openapi-fetch
        // replaces `response` with a returned Response.
        if (wasForced) {
          return new Response(
            JSON.stringify({ error: "error", message: "token expired" }),
            { status: 401, headers: { "content-type": "application/json" } },
          );
        }
      },
    }),
    [logout, forced],
  );
}

type AuthenticatedClientSpec = {
  baseUrl: string;
  select: TokenSelector;
  /** Default: `Authorization: Bearer <token>`. */
  authHeaders?: (token: string) => Record<string, string>;
  /** Extra middleware built from the token; runs before the expiry middleware. */
  middleware?: (token: string | undefined) => Middleware[];
};

/** Rebuilt only when the token (or base URL) changes. */
export function useAuthenticatedFetchClient<Paths extends object>(
  spec: AuthenticatedClientSpec,
): Client<Paths> {
  const token = spec.select(useAssertedAuth());
  const expiry = useSessionExpiryMiddleware();
  return useMemo(() => {
    const client = createFetchClient<Paths>({
      baseUrl: spec.baseUrl,
      credentials: "include",
      headers: token ? (spec.authHeaders ?? bearer)(token) : {},
    });
    client.use(...(spec.middleware?.(token) ?? []), expiry);
    return client;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- spec's functions are per-module constants; baseUrl + token + expiry are the real inputs
  }, [spec.baseUrl, token, expiry]);
}

export function useAuthenticatedApi<Paths extends object>(
  spec: AuthenticatedClientSpec,
) {
  const fetchClient = useAuthenticatedFetchClient<Paths>(spec);
  return useMemo(() => createClient(fetchClient), [fetchClient]);
}
